import * as React from 'react';
import styled from 'styled-components';
import { EmployeeToken, permissionGroupHelper, UserToken } from '../../amorg-common';
import { applicationEndpoints } from '../../amorg-common/applicationEndpoints';
import { clientApi } from '../../api/clientApi';
import { getMaybeActiveEmployeeOrLogin } from '../../api/serverApiHelpers';
import { mediaQuery } from '../../designSystem/BreakPoints';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { Icon } from '../../designSystem/icons/Icons';
import { Footer } from '../../designSystem/layouts/pageWrappers/elements/Footer';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { PageHeader } from '../../designSystem/PageHeader';
import { P } from '../../designSystem/typography/Body';
import { EmployeePortalPageProvider } from '../_contexts/EmployeePortalPageContext';

type EmployeePageWrapperProps = {
    disablePortalStatusCheck?: boolean,
    children: React.ReactNode;
};

export const EmployeePageWrapper: React.FunctionComponent<EmployeePageWrapperProps> = (props: EmployeePageWrapperProps) => 
{
    const [activeUser, setActiveUser] = React.useState<UserToken | EmployeeToken | null>(null);
    const [portalDisabed, setPortalDisabled] = React.useState(false);

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const result = await getMaybeActiveEmployeeOrLogin();
            setActiveUser(result);
            if (!props.disablePortalStatusCheck)
            {
                const options = await clientApi.portal.getLoginOptions(result.siteId);
                setPortalDisabled(!options.enabled);
            }
        };
        getData();
    }, []);

    const allowed = activeUser !== null && !portalDisabed;
    const [menuOpen, setMenuOpen] = React.useState(false);

    const navigationShared = <>
        {activeUser?.type === 'user' && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroupId, 'member') &&
            <LinkButton
                variant={"clean"}
                href={applicationEndpoints.root}
                icon="arrows_arrow_arrowBack"
                text="AMO portalen"
            />
        }
        <LinkButton
            variant={"clean"}
            href={applicationEndpoints.portal}
            icon="navigation_home"
            text="Dashboard"
        />
        <LinkButton
            variant={"clean"}
            href={applicationEndpoints.portal_organization}
            icon="interface_user_team"
            text="Organisationen"
        />
        <LinkButton
            variant={"clean"}
            href={applicationEndpoints.portal_survey_reports}
            icon="list_alt"
            text="Rapporter"
        />
        <LinkButton
            variant={"clean"}
            href={applicationEndpoints.portal_files}
            icon="data_files_document"
            text="Dokumenter og filer"
        />
        <LinkButton
            variant={"clean"}
            href={applicationEndpoints.portal_links}
            icon="interface_actions_link"
            text="Links"
        />
        <LinkButton
            variant={"clean"}
            href={applicationEndpoints.portal_logout}
            icon="logout"
            text="Log ud"
        />
        </>


    return (
        <EmployeePortalPageProvider user={activeUser}>
            <Main className="employeePortalPage">
                <PublicNavigation className="publicNavigation">
                    <Wrapper className="publicNavigationWrapper">
                        <PublicLogo href={applicationEndpoints.portal}>
                            <img src="/images/amorg_logo_blue.svg" />
                        </PublicLogo>

                        {allowed &&
                            <>
                                <DesktopNav>
                                    {navigationShared}
                                </DesktopNav>
                                
                                <BurgerButton onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
                                    <Icon name={menuOpen ? 'actions_close' : 'navigation_menu'} size='medium' />
                                </BurgerButton>
                                <MobileMenu isOpen={menuOpen} onClick={() => setMenuOpen(false)}>
                                    {navigationShared}
                                </MobileMenu>
                            </>
                        }
                    </Wrapper>
                </PublicNavigation>
                <CoreContent className="mainContent">

                    {allowed && props.children}

                    {!allowed &&
                        <SingleColumnLayout
                            noDivider
                            headerContainer={
                                <PageHeader headerText="Hov" headerStyle='primary-black' />
                            }
                            contentContainer={
                                <P>Hov! Du er havnet på en side, som du ikke har adgang til.</P>
                            }
                        />
                    }

                </CoreContent>
                <CoreFooter>
                    <Footer />
                </CoreFooter>
            </Main>
        </EmployeePortalPageProvider>
    );
};

const Main = styled.div`
    
    display:grid;
    min-height: 100%;
    grid-template-columns: auto;
    grid-template-rows: 60px auto min-content;
    grid-template-areas:
        "navigation"
        "content"
        "footer";

    ${mediaQuery.tablet} {
        grid-template-columns: auto;
        grid-template-rows: 80px auto min-content;
        grid-template-areas:
        "navigation"
        "content"
        "footer";
    }
`;

const CoreContent = styled.div`
    background: rgb(237, 237, 237);
    padding-bottom:20px;
    position: relative;
    z-index: 10;
    grid-area: content;
`;

const CoreFooter = styled.div`
    grid-area: footer;
`;

const PublicNavigation = styled.div`
    background: rgb(222, 215, 199);
    align-self: start;
    position: relative;
    height: 50px;
    width: 100%;
    margin:0 auto;
    box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    -webkit-box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    -moz-box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    z-index:1000;
  
    ${mediaQuery.tablet} {
        height: 80px;
        width: 100%;
    }
`;


const Wrapper = styled.div<{ wide?: boolean; }>`
    width:100%;
    margin: auto;
    max-width: ${props => props.wide ? '90%' : '1020px'};
    padding:16px 0px;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    display: flex;
    flex-direction: row;
    padding: 0 10px;
`;


const DesktopNav = styled.div`
    display: none;
    align-items: center;
    gap: 8px;

    ${mediaQuery.tablet} {
        display: flex;
    }
`;

const BurgerButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;

    ${mediaQuery.tablet} {
        display: none;
    }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
    position: absolute;
    top: 50px;
    left: 0;
    right: 0;
    background: rgb(222, 215, 199);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: ${props => props.isOpen ? '500px' : '0'};
    padding: ${props => props.isOpen ? '8px 16px 16px' : '0 16px'};
    box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    z-index: 999;
    align-items: flex-start;
    transition: max-height 0.3s ease, padding 0.3s ease;

    ${mediaQuery.tablet} {
        display: none;
    }
`;

const PublicLogo = styled.a`
    display: flex;
    align-items: center;
    justify-content: start;
    height: 50%;
    width: 50%;

    & img {
        height: 100%;
        width: auto;
        max-width: 100%;
    }
`;